import React, { useState, useEffect, useRef, useCallback } from 'react';

function App() {
  const [city, setCity] = useState('');
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [judging, setJudging] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchedCity, setSearchedCity] = useState('');
  const initialSearchDone = useRef(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // 検索実行（cityを引数で受け取れるようにする）
  const doSearch = useCallback(async (searchCity) => {
    const target = searchCity || city;
    if (!target.trim()) return;

    setCity(target);
    setLoading(true);
    setError(null);
    setAttractions([]);
    setShowFavorites(false);
    setCurrentPage(1);
    setSearchedCity(target);
    setJudging(false);

    try {
      const res1 = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: target, page: 1 }),
      });

      if (!res1.ok) throw new Error('検索に失敗しました');

      const data1 = await res1.json();
      const places = data1.attractions || [];
      setAttractions(places);
      setLoading(false);

      if (data1.phase === 'complete') return;

      setJudging(true);
      const res2 = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: target, page: 1, places: places }),
      });

      if (res2.ok) {
        const data2 = await res2.json();
        setAttractions(data2.attractions || []);
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    } finally {
      setJudging(false);
    }
  }, [API_URL, city]);

  // 初回マウント：お気に入り読み込み + URLパラメータからの自動検索
  useEffect(() => {
    const saved = localStorage.getItem('tripcheck_favorites');
    if (saved) {
      try { setFavorites(JSON.parse(saved)); } catch (e) { /* ignore */ }
    }

    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q && !initialSearchDone.current) {
      initialSearchDone.current = true;
      window.history.replaceState({}, '', '/');
      setCity(q);
      // 直接API呼び出し（stateに依存しない）
      (async () => {
        setLoading(true);
        setSearchedCity(q);
        try {
          const res1 = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ city: q, page: 1 }),
          });
          if (!res1.ok) throw new Error('検索に失敗しました');
          const data1 = await res1.json();
          const places = data1.attractions || [];
          setAttractions(places);
          setLoading(false);

          if (data1.phase === 'complete') return;

          setJudging(true);
          const res2 = await fetch(process.env.REACT_APP_API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ city: q, page: 1, places: places }),
          });
          if (res2.ok) {
            const data2 = await res2.json();
            setAttractions(data2.attractions || []);
          }
        } catch (err) {
          setError(err.message);
          setLoading(false);
        } finally {
          setJudging(false);
        }
      })();
    }
  }, []);

  // ホームに戻る
  const goHome = () => {
    setAttractions([]);
    setShowFavorites(false);
    setCity('');
    setError(null);
    setSelectedTab('all');
    setJudging(false);
    setLoading(false);
  };

  // お気に入り
  const saveFavorites = (newFavorites) => {
    setFavorites(newFavorites);
    localStorage.setItem('tripcheck_favorites', JSON.stringify(newFavorites));
  };

  const toggleFavorite = (attraction) => {
    const exists = favorites.find(f => f.id === attraction.id);
    if (exists) {
      saveFavorites(favorites.filter(f => f.id !== attraction.id));
    } else {
      saveFavorites([...favorites, { ...attraction, savedAt: Date.now() }]);
    }
  };

  const isFavorite = (id) => favorites.some(f => f.id === id);

  // もっと見る
  const loadMore = async () => {
    const nextPage = currentPage + 1;
    setLoadingMore(true);

    try {
      const res1 = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: searchedCity, page: nextPage }),
      });

      if (!res1.ok) throw new Error('追加検索に失敗しました');

      const data1 = await res1.json();
      const newPlaces = data1.attractions || [];
      const existingIds = new Set(attractions.map(a => a.id));
      const unique = newPlaces.filter(a => !existingIds.has(a.id));
      setAttractions(prev => [...prev, ...unique]);
      setCurrentPage(nextPage);
      setLoadingMore(false);

      if (data1.phase === 'complete') return;

      setJudging(true);
      const res2 = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: searchedCity, page: nextPage, places: newPlaces }),
      });

      if (res2.ok) {
        const data2 = await res2.json();
        const judgedPlaces = data2.attractions || [];
        setAttractions(prev => {
          const base = prev.filter(a => !newPlaces.some(np => np.id === a.id));
          return [...base, ...judgedPlaces];
        });
      }
    } catch (err) {
      setError(err.message);
      setLoadingMore(false);
    } finally {
      setJudging(false);
    }
  };

  // ステータス表示
  const getStatusEmoji = (s) => s === 'required' ? '🔴' : s === 'recommended' ? '🟡' : s === 'notRequired' ? '🟢' : s === 'unknown' ? '⏳' : '⚪';
  const getStatusText = (s) => s === 'required' ? '予約必須' : s === 'recommended' ? '予約推奨' : s === 'notRequired' ? '予約不要' : s === 'unknown' ? 'AI分析中...' : '不明';
  const getStatusClass = (s) => s === 'required' ? 'status-required' : s === 'recommended' ? 'status-recommended' : s === 'notRequired' ? 'status-not-required' : '';

  const displayList = showFavorites ? favorites : attractions;
  const filteredAttractions = displayList.filter(a => selectedTab === 'all' || a.reservationRequired === selectedTab);
  const countByStatus = (status) => displayList.filter(a => a.reservationRequired === status).length;

  const renderCard = (attraction) => (
    <div key={attraction.id} className={"attraction-card " + getStatusClass(attraction.reservationRequired)}>
      <button
        className={"fav-btn" + (isFavorite(attraction.id) ? " fav-active" : "")}
        onClick={() => toggleFavorite(attraction)}
        title={isFavorite(attraction.id) ? "お気に入りから削除" : "お気に入りに追加"}
      >
        {isFavorite(attraction.id) ? "\u2665" : "\u2661"}
      </button>
      <div className="card-body">
        <div className="card-info">
          <div className="card-header">
            <h3>{attraction.name}</h3>
            <span className={"status-badge " + getStatusClass(attraction.reservationRequired)}>
              {getStatusEmoji(attraction.reservationRequired)} {getStatusText(attraction.reservationRequired)}
            </span>
          </div>
          <p className="category">{attraction.category}</p>
          {attraction.rating && <p className="rating">{attraction.rating}</p>}
          {attraction.reason && <p className="reason">{attraction.reason}</p>}
          {attraction.bookingTiming && <p className="booking-timing">{attraction.bookingTiming}</p>}
          <div className="card-details">
            {attraction.address && <p className="address">{attraction.address}</p>}
            {attraction.price && <p className="price">{attraction.price}</p>}
            {attraction.estimatedDuration && <p className="duration">{attraction.estimatedDuration}</p>}
          </div>
          {attraction.tips && <p className="tips">{attraction.tips}</p>}
        </div>
        {attraction.reservationLinks && attraction.reservationLinks.length > 0 && (
          <div className="booking-links">
            <p className="booking-label">予約サイトを比較</p>
            {attraction.reservationLinks.map((link, idx) => (
              <a key={idx} href={link.url || link} target="_blank" rel="noopener noreferrer"
                className={"booking-link booking-" + (link.type || 'default')}>
                <span className="booking-name">
                  {link.type === 'official' && '🏛 '}{link.name || '予約サイト'}
                </span>
                <span className="booking-arrow">{"\u203A"}</span>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const isHome = !showFavorites && !loading && attractions.length === 0 && !error;

  return (
    <div className="app">
      <header className="header" onClick={goHome} style={{cursor:'pointer'}}>
        <div className="header-icon">✈️</div>
        <h1>旅行予約チェック</h1>
        <p className="subtitle">必要な施設の予約、もれちゃってない？？</p>
      </header>

      <div className="search-section">
        <div className="search-box">
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="都市名を入力（例：パリ、東京、バルセロナ）"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            />
          </div>
          <button className="search-btn" onClick={() => doSearch()} disabled={loading}>
            {loading ? '検索中...' : '検索する'}
          </button>
        </div>
      </div>

      <div className="view-toggle">
        <button className={"toggle-btn" + (!showFavorites ? " active" : "")} onClick={() => setShowFavorites(false)}>
          🔍 検索結果
        </button>
        <button className={"toggle-btn" + (showFavorites ? " active" : "")} onClick={() => setShowFavorites(true)}>
          {"\u2665"} お気に入り ({favorites.length})
        </button>
      </div>

      {displayList.length > 0 && (
        <div className="tabs">
          <button className={selectedTab === 'all' ? 'tab active' : 'tab'} onClick={() => setSelectedTab('all')}>すべて ({displayList.length})</button>
          <button className={selectedTab === 'required' ? 'tab active tab-required' : 'tab'} onClick={() => setSelectedTab('required')}>🔴 予約必須 ({countByStatus('required')})</button>
          <button className={selectedTab === 'recommended' ? 'tab active tab-recommended' : 'tab'} onClick={() => setSelectedTab('recommended')}>🟡 予約推奨 ({countByStatus('recommended')})</button>
          <button className={selectedTab === 'notRequired' ? 'tab active tab-not-required' : 'tab'} onClick={() => setSelectedTab('notRequired')}>🟢 予約不要 ({countByStatus('notRequired')})</button>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>観光施設を検索中...</p>
        </div>
      )}

      {judging && (
        <div className="judging-banner">
          <span className="spinner-small"></span>
          AIが予約の必要性を分析中... まもなく結果が更新されます
        </div>
      )}

      {showFavorites && favorites.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">{"\u2661"}</div>
          <p>まだお気に入りがありません</p>
          <p className="empty-sub">気になる施設の {"\u2661"} をタップして保存しましょう</p>
        </div>
      )}

      {isHome && (
        <div className="landing">
          <div className="popular-cities">
            <h2>人気の都市から探す</h2>
            <div className="city-buttons">
              {[
                { name: 'パリ', slug: 'paris', flag: '🇫🇷' },
                { name: 'ソウル', slug: 'seoul', flag: '🇰🇷' },
                { name: '台北', slug: 'taipei', flag: '🇹🇼' },
                { name: 'バンコク', slug: 'bangkok', flag: '🇹🇭' },
                { name: 'ハワイ', slug: 'hawaii', flag: '🇺🇸' },
                { name: 'バルセロナ', slug: 'barcelona', flag: '🇪🇸' },
                { name: 'ローマ', slug: 'rome', flag: '🇮🇹' },
                { name: 'ロンドン', slug: 'london', flag: '🇬🇧' },
                { name: 'ニューヨーク', slug: 'new-york', flag: '🇺🇸' },
                { name: 'シンガポール', slug: 'singapore', flag: '🇸🇬' },
                { name: '東京', slug: 'tokyo', flag: '🇯🇵' },
                { name: '京都', slug: 'kyoto', flag: '🇯🇵' },
              ].map(c => (
                <a key={c.slug} href={"/city/" + c.slug} className="city-btn">
                  <span className="city-flag">{c.flag}</span> {c.name}
                </a>
              ))}
            </div>
            <a href="/city/" className="all-cities-link">すべての都市・国を見る</a>
          </div>

          <div className="how-it-works">
            <h2>使い方</h2>
            <div className="steps">
              <div className="step"><div className="step-num">1</div><div className="step-text"><strong>行き先を入力</strong><span>都市名や国名を入力して検索</span></div></div>
              <div className="step"><div className="step-num">2</div><div className="step-text"><strong>AIが自動判定</strong><span>観光施設の予約必要性をAIが分析</span></div></div>
              <div className="step"><div className="step-num">3</div><div className="step-text"><strong>一目でわかる</strong><span>🔴必須 🟡推奨 🟢不要 がすぐわかる</span></div></div>
            </div>
          </div>

          <div className="sample-results">
            <h2>検索結果イメージ</h2>
            <div className="phone-frame">
              <div className="phone-notch"></div>
              <div className="phone-screen">
                <div className="mock-header">✈️ 旅行予約チェック</div>
                <div className="mock-search"><span className="mock-search-text">🔍 パリ</span><span className="mock-search-btn">検索する</span></div>
                <div className="mock-tabs"><span className="mock-tab active">すべて (15)</span><span className="mock-tab req">🔴 5</span><span className="mock-tab rec">🟡 6</span><span className="mock-tab nr">🟢 4</span></div>
                <div className="mock-cards">
                  <div className="mock-card mock-required"><div className="mock-card-top"><strong>エッフェル塔</strong><span className="mock-badge mock-badge-req">🔴 予約必須</span></div><p className="mock-reason">展望台に登るには時間指定の予約が実質必須</p><span className="mock-timing">📅 2〜3ヶ月前の予約を推奨</span><div className="mock-meta">💰 €35 ⏱ 2〜3時間</div></div>
                  <div className="mock-card mock-required"><div className="mock-card-top"><strong>ルーブル美術館</strong><span className="mock-badge mock-badge-req">🔴 予約必須</span></div><p className="mock-reason">時間指定のオンライン予約が必須</p><span className="mock-timing">📅 1〜2週間前の予約を推奨</span><div className="mock-meta">💰 €22 ⏱ 2〜3時間</div></div>
                  <div className="mock-card mock-recommended"><div className="mock-card-top"><strong>凱旋門</strong><span className="mock-badge mock-badge-rec">🟡 予約推奨</span></div><p className="mock-reason">展望台は予約でスキップ可能</p><div className="mock-meta">💰 €16 ⏱ 1時間</div></div>
                  <div className="mock-card mock-not-required"><div className="mock-card-top"><strong>サクレ・クール寺院</strong><span className="mock-badge mock-badge-nr">🟢 予約不要</span></div><p className="mock-reason">入場無料、予約不要</p><div className="mock-meta">💰 無料 ⏱ 30分</div></div>
                </div>
              </div>
            </div>
            <p className="sample-cta"><a href="/city/paris">パリの全施設を見る</a></p>
          </div>
        </div>
      )}

      <div className="content-with-ads">
        <aside className="ad-sidebar ad-left"><div className="ad-slot"><span className="ad-placeholder">広告</span></div></aside>
        <div className="content-main">
          <div className="attractions-grid">{filteredAttractions.map(renderCard)}</div>
          {filteredAttractions.length === 0 && displayList.length > 0 && (
            <div className="no-results">このフィルターに該当する施設はありません</div>
          )}
          {!showFavorites && attractions.length > 0 && !loading && currentPage < 2 && (
            <div className="load-more-section">
              <button className="load-more-btn" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? (<><span className="spinner-small"></span>追加検索中...</>) : '近郊・周辺スポットをもっと見る'}
              </button>
              <p className="load-more-hint">世界遺産や日帰り観光地なども検索します</p>
            </div>
          )}
        </div>
        <aside className="ad-sidebar ad-right"><div className="ad-slot"><span className="ad-placeholder">広告</span></div></aside>
      </div>

      <footer className="footer"><p>旅行予約チェック</p></footer>
    </div>
  );
}

export default App;
